/**
 * Ajustar o limite de segurança de um grupo.
 *
 * O campo mostra, ao lado, **o que aquele número significa agora** — quantas vagas sobram
 * com a contagem atual, e se o grupo sai do rodízio na hora de salvar. Número de operação
 * que a pessoa digita sem ver o efeito é como se erra por uma ordem de grandeza sem que
 * nada quebre.
 */
import { useEffect, useState } from 'react';
import { Button, Dialog, Field, Input } from '@mgdlead/ui';

import { FormAlert } from '@mgdlead/ui';
import { groupDisplayName } from '../../../features/group/group-ui';
import type { Group } from '../../../lib/api';

export interface SafetyThresholdDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: Group | null;
  pending: boolean;
  onConfirm: (value: number) => void;
}

export function SafetyThresholdDialog({
  open,
  onOpenChange,
  group,
  pending,
  onConfirm,
}: SafetyThresholdDialogProps) {
  const [value, setValue] = useState<string>('');

  useEffect(() => {
    if (open && group) setValue(String(group.safetyThreshold));
  }, [open, group]);

  if (!group) return null;

  const parsed = Number.parseInt(value, 10);
  const valido = Number.isFinite(parsed) && parsed >= 1 && parsed <= group.maxParticipants;
  const erro = valido
    ? undefined
    : `Escolha um número entre 1 e ${group.maxParticipants} (a capacidade do grupo).`;

  const count = group.participantsCount;
  const ficaCheio = valido && count !== null && count >= parsed;
  const vagas = valido && count !== null ? Math.max(0, parsed - count) : null;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Limite de segurança"
      description={`Em “${groupDisplayName(group)}”. O grupo sai do rodízio ao bater este número.`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={pending}
            loadingLabel="Salvando"
            disabled={!valido}
            onClick={() => onConfirm(parsed)}
          >
            Salvar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field
          label="Parar de mandar gente ao chegar em"
          hint={`A capacidade do WhatsApp é ${group.maxParticipants}. A folga existe porque a contagem é sempre um retrato atrasado: entre o último aviso e o próximo clique cabe gente entrando.`}
          error={erro}
          required
        >
          {(control) => (
            <Input
              {...control}
              numeric
              inputMode="numeric"
              value={value}
              invalid={!valido}
              onChange={(event) => setValue(event.target.value)}
            />
          )}
        </Field>

        {/* O eco da conta: o número digitado traduzido no efeito que ele tem hoje. */}
        {count === null ? (
          <p className="text-xs text-muted-foreground">
            Este grupo ainda não foi sincronizado, então não dá para dizer quantas vagas sobram.
          </p>
        ) : ficaCheio ? (
          <FormAlert tone="warning">
            Com {count} participantes hoje, o grupo passa a contar como cheio e sai do rodízio assim
            que você salvar.
          </FormAlert>
        ) : vagas !== null ? (
          <p className="nums text-xs text-muted-foreground">
            Com {count} participantes hoje, sobram <span className="text-foreground">{vagas}</span>{' '}
            {vagas === 1 ? 'vaga' : 'vagas'}.
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
